import { useLocation, useNavigate } from '@solidjs/router';
import { Show, createEffect, type JSX } from 'solid-js';
import { isAuthenticated, isLoading, getUser } from '../store/session.store';
import { getBusiness, isBusinessLoaded } from '@/shared/stores/business.store';

interface ProtectedRouteProps {
  children: JSX.Element;
}

/**
 * Route guard for authenticated routes
 *
 * Shows loading state while checking session.
 * Redirects to /login if not authenticated.
 * Redirects to /no-business if user has no business (edge case for legacy accounts).
 *
 * Implementation note: We use createEffect + navigate() instead of <Navigate>
 * components as Show fallbacks. <Navigate> calls navigate() synchronously
 * during render, which races with the same Show that triggered the fallback
 * being unmounted by the route transition — causing
 * "insertBefore: node is not a child of this node" crashes when reactive
 * computations inside the protected tree (resources, effects) try to update
 * DOM that's being concurrently torn down.
 */
export function ProtectedRoute(props: ProtectedRouteProps) {
  const location = useLocation();
  const navigate = useNavigate();

  /**
   * Check if user has a business.
   * Uses the user's `business` field from the session (set at login/signup).
   * Also checks the business store as a secondary signal.
   */
  const hasBusiness = () => {
    // If the business store has been loaded, trust it as the source of truth.
    // This handles the case where a business was just deleted (store is null,
    // but the stale JWT user object may still have the old business ID).
    if (isBusinessLoaded()) return !!getBusiness();
    // Store not loaded yet — use the JWT user's business field as an early hint.
    const u = getUser();
    return !!u?.business;
  };

  // Effect-based redirects: run AFTER render commits, so the route transition
  // can cleanly tear down the current tree without racing in-flight effects.
  createEffect(() => {
    if (isLoading()) return;
    if (!isAuthenticated()) {
      const redirect = encodeURIComponent(location.pathname + location.search);
      navigate(`/login?redirect=${redirect}`, { replace: true });
      return;
    }
    if (!hasBusiness()) {
      navigate('/no-business', { replace: true });
    }
  });

  return (
    <Show
      when={!isLoading() && isAuthenticated() && hasBusiness()}
      fallback={
        <div class="flex h-screen items-center justify-center bg-bg-app">
          <svg
            class="h-7 w-7 animate-spin text-text-muted"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="3"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        </div>
      }
    >
      {props.children}
    </Show>
  );
}
